import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HTTP } from '@ionic-native/http';
import { Platform } from 'ionic-angular';

import { APP_CONFIG } from '../../app.config';
import { HttpMethod } from '../enums/http-methods.enum';
import { HttpHelper } from '../helpers/http.helper';
import { Error } from '../models/error.model';
import { ConnectivityService } from './connectivity.service';
import { UserService } from './user.service';

@Injectable()
export class RequestService {
  private header: any;
  private userService: UserService;
  private token: string;

  private httpHelper: HttpHelper;

  constructor(
    private connectivityService: ConnectivityService,
    private httpClient: HttpClient,
    private httpNative: HTTP,
    private platform: Platform
  ) {
    this.httpHelper = new HttpHelper(platform, httpClient, httpNative);
  }

  //Para contornar erros de circular dependency
  setUserService(service: UserService): void {
    this.userService = service;
  }

  setAuthToken(token: string): void {
    this.token = token;
  }

  getAuthToken(): string {
    return this.token;
  }

  getData(url: string, forceUseHttpClient?: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      this.checkConnection().then(
        () => {
          const getRequest: VoidFunction = async () => {
            this.httpHelper
              .get(url, await this.getRequestOptions(url), forceUseHttpClient)
              .then(
                (resp) => {
                  this.header = null;
                  resolve(this.getJsonData(resp));
                },
                (error) => {
                  this.catchError(error, url, getRequest, reject, HttpMethod.GET);
                }
              )
              .catch((error) => {
                this.catchError(error, url, getRequest, reject, HttpMethod.GET);
              });
          };
          getRequest();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  putData(url: string, data?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.checkConnection().then(
        () => {
          const putRequest: VoidFunction = async () => {
            this.httpHelper
              .put(url, data || {}, await this.getRequestOptions(url))
              .then(
                (resp) => {
                  this.header = null;
                  resolve(this.getJsonData(resp));
                },
                (error) => {
                  this.catchError(error, url, putRequest, reject, HttpMethod.PUT, data);
                }
              )
              .catch((error) => {
                this.catchError(error, url, putRequest, reject, HttpMethod.PUT, data);
              });
          };
          putRequest();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  patchData(url: string, data?: any, returnLocation?: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      this.checkConnection().then(
        () => {
          const patchRequest: VoidFunction = async () => {
            this.httpHelper
              .patch(url, data || {}, await this.getRequestOptions(url))
              .then(
                (resp) => {
                  this.header = null;
                  if (returnLocation) {
                    resolve({
                      location: this.platform.is('cordova')
                        ? resp.headers['location']
                        : resp.headers.get('Location'),
                      data: this.getJsonData(resp.body || resp),
                    });
                  } else {
                    resolve(this.getJsonData(resp.body || resp));
                  }
                },
                (error) => {
                  this.catchError(error, url, patchRequest, reject, HttpMethod.PATCH, data);
                }
              )
              .catch((error) => {
                this.catchError(error, url, patchRequest, reject, HttpMethod.PATCH, data);
              });
          };
          patchRequest();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  postData(url: string, data?: any, returnLocation?: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      this.checkConnection().then(
        () => {
          const postRequest: VoidFunction = async () => {
            this.httpHelper
              .post(url, data || {}, await this.getRequestOptions(url))
              .then(
                (resp) => {
                  this.header = null;
                  if (returnLocation) {
                    resolve({
                      location: this.platform.is('cordova')
                        ? resp.headers['location']
                        : resp.headers.get('Location'),
                      data: this.getJsonData(resp.body || resp),
                    });
                  } else {
                    resolve(this.getJsonData(resp.body || resp));
                  }
                },
                (error) => {
                  this.catchError(error, url, postRequest, reject, HttpMethod.POST, data);
                }
              )
              .catch((error) => {
                this.catchError(error, url, postRequest, reject, HttpMethod.POST, data);
              });
          };
          postRequest();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  deleteData(url: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.checkConnection().then(
        () => {
          const deleteRequest: VoidFunction = async () => {
            this.httpHelper
              .delete(url, await this.getRequestOptions(url))
              .then(
                (resp) => {
                  this.header = null;
                  resolve(this.getJsonData(resp));
                },
                (error) => {
                  this.catchError(error, url, deleteRequest, reject, HttpMethod.DELETE);
                }
              )
              .catch((error) => {
                this.catchError(error, url, deleteRequest, reject, HttpMethod.DELETE);
              });
          };
          deleteRequest();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  async downloadFile(url: string, destFilename: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.checkConnection().then(
        () => {
          const downloadRequest: VoidFunction = async () => {
            this.httpHelper
              .download(url, destFilename, await this.getRequestOptions(url))
              .then(
                (resp) => {
                  this.header = null;
                  if (!resp) throw new Error(-1, 'Falha ao baixar o arquivo.');

                  if (this.platform.is('cordova')) {
                    resolve(resp);
                  } else {
                    const dataType = resp.type;
                    const binaryData = [resp];
                    const downloadLink = document.createElement('a');
                    downloadLink.href = window.URL.createObjectURL(
                      new Blob(binaryData, { type: dataType })
                    );
                    if (destFilename) {
                      downloadLink.setAttribute('download', destFilename);
                    }
                    downloadLink.click();
                    resolve(resp);
                  }
                },
                (error) => {
                  this.catchError(error, url, downloadRequest, reject, HttpMethod.GET);
                }
              )
              .catch((error) => {
                this.catchError(error, url, downloadRequest, reject, HttpMethod.GET);
              });
          };
          downloadRequest();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Contrói a url com base nos parametros já definidos na url com {key}
   */
  buildUrlPathParams(params: any, urlRequest: string): string {
    if (params == null) return null;

    for (const key in params) {
      if (params[key] != null) {
        urlRequest = urlRequest.replace('{' + key + '}', encodeURIComponent(params[key]));
      }
    }
    console.log('urlRequest: ' + urlRequest);
    return urlRequest;
  }

  /**
   * Contrói os parâmetros para query params
   */
  buildUrlParams(params: any): string {
    if (params == null) return null;

    const urlParams: URLSearchParams = new URLSearchParams();
    for (const key in params) {
      if (params[key] != null) {
        urlParams.append(key, params[key]);
      }
    }
    return urlParams.toString();
  }

  /**
   * Adiciona properties no header da requisição. Passar os campos chave-valor.
   * Exemplo: key = 'Content-Type'; value = 'application/json'
   */
  addParamsHeader(key: string, value: string): void {
    if (!this.header) {
      this.header = {};
    }
    if (this.header[key]) {
      delete this.header[key];
    }
    this.header[key] = value;
  }

  /* Private métodos
	================================*/

  private catchError(
    error: any,
    url: string,
    retryCallback: VoidFunction,
    reject: any,
    method: HttpMethod,
    data?: any
  ): void {
    this.header = null;
    this.treatError(error, url, method, data).then((e: any) => {
      if (e.tryAgain) {
        retryCallback();
      } else {
        reject(e);
      }
    });
  }

  private checkConnection(): Promise<any> {
    if (!this.connectivityService.isOnline()) {
      return Promise.reject({
        status: APP_CONFIG.ERRORS.OFFLINE,
        message: 'ERRORS.OFFLINE',
      });
    }
    return Promise.resolve();
  }

  private getJsonData(response: any): any {
    let body: any = null;
    try {
      if (this.platform.is('cordova')) {
        if (response.data != null) {
          body = JSON.parse(response.data);
        } else {
          body = typeof response === 'string' ? JSON.parse(response) : response;
        }
      } else {
        body = response;
      }
    } catch {
      //sem content no response
    }
    return body;
  }

  private treatError(
    error: HttpErrorResponse | any,
    urlRequest: string,
    method: HttpMethod,
    data?: any
  ): Promise<Error> {
    return new Promise((resolve) => {
      console.error(JSON.stringify(error));
      const errorApp: Error = new Error(0, 'ERRORS.GENERIC');

      if (error.status) {
        // 401 (Não autorizado - Usuario e ou senha inválido, token expirado)
        // 403 (Acesso negado ao acessar o recurso, scope inválido)
        switch (error.status) {
          case 500:
            errorApp.message = 'ERRORS.SERVER_ERROR';
            this.sendLogError(method, urlRequest, error, data);
            break;

          case 503:
          case 502:
            errorApp.message = 'ERRORS.SERVICE_UNAVAILABLE';
            break;

          case 504:
            errorApp.message = 'ERRORS.TIMEOUT';
            break;

          case 403:
            errorApp.message = 'ERRORS.ACCESS_DENIED';
            // Recarrego a página para evitar possíveis problemas de ter 2 abas com stores diferentes
            document.location.reload();
            return;

          default:
            break;
        }
        errorApp.status = error.status;
      }

      if (error && error.name && error.name === 'TimeoutError') {
        errorApp.message = 'ERRORS.TIMEOUT';
      } else {
        const body: any = this.getJsonData(error.error || error.data);

        if (body) {
          errorApp.fullError = body;
          if (body.errorMessage || body.message || body.errors) {
            errorApp.message = body.errorMessage || body.message || body.errors;
          }
        }
      }

      if (
        (errorApp.status === 401 && !this.isAuthRequest(urlRequest)) ||
        (errorApp.status === 412 && errorApp.message === 'USER.SHOULD_LOGIN_AGAIN')
      ) {
        this.userService.refreshToken().then(
          () => {
            errorApp.tryAgain = true;
            resolve(errorApp);
          },
          (tokenErrorResponse) => {
            resolve(tokenErrorResponse);
          }
        );
      } else if (errorApp.status === 400 && this.isAuthRequest(urlRequest)) {
        //Springboot retorna 400 tbm quando o refresh token é inválido.
        //Para tratar tudo como 401 na controller, altero o status code para 401
        errorApp.status = 401;
        resolve(errorApp);
      } else {
        resolve(errorApp);
      }
    });
  }

  private sendLogError(method: HttpMethod, urlRequest: string, error: any, data?: any): void {
    try {
      const body: any = this.getJsonData(error.error || error.data);
      const apiUrl: string = urlRequest.split('/api/')[1];
      const json: string = data ? JSON.stringify(data) : null;
      const params: any = {
        requestId: body.requestId,
        url: apiUrl,
        method: method,
        data: json,
      };
      this.postData(APP_CONFIG.WEBSERVICE.LOG_ERROR, params);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Retorna o cabeçalho com os parametros setados para a requisição.
   */
  private async getRequestOptions(urlRequest: string): Promise<any> {
    let options: any = null;
    if (!this.isAuthRequest(urlRequest) && !this.isExternalRequest(urlRequest)) {
      await this.addUserTokenToHeader();
    }
    if (this.isFastgetApi(urlRequest)) {
      this.addParamsHeader('X-Api-Key', APP_CONFIG.API_KEY);
    }
    if (this.header) {
      options = this.platform.is('cordova') ? this.header : new HttpHeaders(this.header);
    }
    return options;
  }

  private async addUserTokenToHeader(): Promise<void> {
    if (this.token) {
      let expiresAt = localStorage.getItem('expiresAt');
      if (expiresAt) {
        let expirationDate = Number(expiresAt);
        let now = new Date().getTime();
        if (now > expirationDate) {
          console.log('Token expirado');
          await this.userService.refreshToken().catch((error) => console.error(error));
        }
      }
      this.addParamsHeader('Authorization', 'Bearer ' + this.token);
    }
  }

  private isAuthRequest(urlRequest: string): boolean {
    if (!urlRequest) return false;
    return urlRequest.indexOf(APP_CONFIG.WEBSERVICE.AUTH_TOKEN) !== -1;
  }

  private isExternalRequest(urlRequest: string): boolean {
    if (!urlRequest) return false;
    return (
      urlRequest.indexOf('https://viacep.com.br/ws/') !== -1 ||
      urlRequest.indexOf('/public/') !== -1
    );
  }

  private isFastgetApi(urlRequest: string): boolean {
    if (!urlRequest) return false;
    return urlRequest.indexOf('/proxy/') != -1 || urlRequest.indexOf('api.fastget.com.br/') != -1;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/request.service.ts